// src/components/SignInModal.js
//
// Shared "Returning? Sign in" modal for the calculator and triage intro
// screens. Replaces two hand-duplicated SignInModal components (one built
// inside CalculatorPage.js, one inside TriagePage.js) that each re-implemented
// the same magic-link flow independently. Now there is exactly one sign-in
// UI (OtpAuth) and one modal shell around it.
//
// Like AuthPage.js (/auth) and unlike the ResultCTA save flow — where
// signInWithOtp is allowed to silently create a new account — this modal is
// specifically the "I already have an account" entry point. A first-time
// visitor clicking "Returning? Sign in" and accidentally spinning up a
// bare, subject-less account would be a confusing dead end, not a helpful
// shortcut. So this keeps a real gate: check `profiles` for the email
// before letting OtpAuth send a code. That gate is passed into OtpAuth via
// `beforeSend` rather than built as a second, separate email form — one
// email field, one place that owns the send/verify mechanics, gate is
// just an injected check in front of it.
//
// Copy differs slightly per product (what you're "returning to"), so it's
// centralized here as one small config object instead of being re-typed at
// each call site. Add a new product's copy here, not in the page files.
// The actual "must already have an account" check lives in
// src/lib/accountGate.js, shared with AuthPage.js — this file only owns
// per-product copy, not the gate logic itself.
//
// onSuccess(session) — parent decides what happens next (this component
// doesn't navigate, matching OtpAuth's own contract).

import React from 'react'
import ModalShell from './ModalShell'
import OtpAuth from './OtpAuth'
import { requireExistingAccount } from '../lib/accountGate'

const SIGNIN_COPY = {
  calculator: {
    heading: 'Access your saved estimate',
    subheading: "Enter your email — we'll send a code to sign in. No password needed.",
    notFound: 'No account found for that email. Save an estimate first to create your account.',
  },
  triage: {
    heading: 'Access your saved snapshot',
    subheading: "Enter your email — we'll send a code to sign in. No password needed.",
    notFound: 'No account found for that email. Complete the intake form first to get started.',
  },
  // Used by StartPage.js's AnonHeader, which isn't tied to one journey —
  // a visitor there could be returning to either product. Falls back to
  // this instead of the calculator-specific copy above (which the
  // `SIGNIN_COPY[product] || SIGNIN_COPY.calculator` default previously
  // fell through to, silently, for any unrecognized product key).
  default: {
    heading: 'Sign in to HolaSam',
    subheading: "Enter your email — we'll send a code to sign in. No password needed.",
    notFound: 'No account found for that email. Complete your Snapshot or estimate first to create your account.',
  },
}

export default function SignInModal({ product, onClose, onSuccess }) {
  // Two-tier fallback: a recognized-but-uncovered product (e.g. 'autonomo'
  // — there's no dedicated entry for it, and "Access your saved estimate"
  // already describes it accurately) falls back to calculator's copy,
  // same as before this change. Only a page with NO product at all
  // (StartPage.js, not tied to one journey) falls back to the new
  // generic 'default' entry.
  const copy = SIGNIN_COPY[product] || (product ? SIGNIN_COPY.calculator : SIGNIN_COPY.default)

  function beforeSend(email) {
    return requireExistingAccount(email, copy.notFound)
  }

  return (
    <ModalShell onClose={onClose}>
      <h2 className="signin-modal-title">{copy.heading}</h2>
      <p className="signin-modal-body">{copy.subheading}</p>
      <OtpAuth beforeSend={beforeSend} onSuccess={onSuccess} submitLabel="Sign in" />
    </ModalShell>
  )
}